/**
 * Suggested Ritual Section
 * Handles add-to-cart functionality for individual items and kit products
 * Compatible with Shopify OS 2.0
 */

class SuggestedRitualSection {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.productId = container.dataset.productId;

    this.init();
  }

  init() {
    this.bindEvents();
  }

  /**
   * Bind event listeners to buttons
   */
  bindEvents() {
    // Kit add-to-cart button only (individual items handled by theme)
    this.kitButton = this.container.querySelector('[data-action="add-kit"]');
    if (this.kitButton) {
      this.kitButton.addEventListener('click', this.handleAddKit.bind(this));
    }
  }

  /**
   * Handle adding kit to cart
   * @param {Event} event - Click event
   */
  async handleAddKit(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const variantId = button.dataset.kitVariantId;

    if (!variantId || button.disabled) {
      return;
    }

    // Set loading state
    this.setButtonLoading(button, true);

    try {
      const response = await this.addToCart(variantId, 1);

      if (response.ok) {
        const data = await response.json();
        this.onAddToCartSuccess(data, button);
      } else {
        const errorData = await response.json();
        this.onAddToCartError(errorData, button);
      }
    } catch (error) {
      console.error('Error adding kit to cart:', error);
      this.onAddToCartError(error, button);
    } finally {
      this.setButtonLoading(button, false);
    }
  }

  /**
   * Add item to cart via Shopify AJAX API
   * @param {string} variantId - Variant ID to add
   * @param {number} quantity - Quantity to add
   * @returns {Promise<Response>}
   */
  addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    });
  }

  /**
   * Handle successful add to cart
   * @param {Object} data - Response data from Shopify
   * @param {HTMLElement} button - Button element
   */
  onAddToCartSuccess(data, button) {
    // Dispatch custom event for theme or other scripts to listen to
    document.dispatchEvent(new CustomEvent('cart:item-added', {
      detail: {
        product: data,
        source: 'suggested-ritual'
      }
    }));

    // Trigger cart refresh if theme supports it
    if (typeof window.refreshCart === 'function') {
      window.refreshCart();
    }

    // Dispatch Shopify's cart update event
    document.dispatchEvent(new CustomEvent('cart:updated'));

    // Visual feedback
    this.showSuccessFeedback(button);
  }

  /**
   * Handle add to cart error
   * @param {Object} error - Error data
   * @param {HTMLElement} button - Button element
   */
  onAddToCartError(error, button) {
    console.error('Add to cart error:', error);

    // Show error message
    const errorMessage = error.description || error.message || 'Unable to add item to cart';
    this.showErrorFeedback(button, errorMessage);
  }

  /**
   * Set button loading state
   * @param {HTMLElement} button - Button element
   * @param {boolean} isLoading - Loading state
   */
  setButtonLoading(button, isLoading) {
    if (isLoading) {
      button.classList.add('loading');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      button.classList.remove('loading');
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * Show success feedback
   * @param {HTMLElement} button - Button element
   */
  showSuccessFeedback(button) {
    const originalHTML = button.innerHTML;
    button.textContent = '✓ Added';
    button.classList.add('success');

    setTimeout(() => {
      button.innerHTML = originalHTML;
      button.classList.remove('success');
    }, 2000);
  }

  /**
   * Show error feedback
   * @param {HTMLElement} button - Button element
   * @param {string} message - Error message
   */
  showErrorFeedback(button, message) {
    // Simple alert for now - can be replaced with custom modal/toast
    alert(message);
  }

  /**
   * Cleanup event listeners
   */
  destroy() {
    if (this.kitButton) {
      this.kitButton.removeEventListener('click', this.handleAddKit.bind(this));
    }
  }
}

/**
 * Initialize sections on page load and in theme editor
 */
function initSuggestedRitualSections() {
  const sections = document.querySelectorAll('.suggested-ritual');

  sections.forEach(section => {
    if (!section.suggestedRitualInstance) {
      section.suggestedRitualInstance = new SuggestedRitualSection(section);
    }
  });
}

/**
 * Cleanup section instance
 * @param {string} sectionId - Section ID to cleanup
 */
function cleanupSuggestedRitualSection(sectionId) {
  const section = document.querySelector(`[data-section-id="${sectionId}"]`);

  if (section && section.suggestedRitualInstance) {
    section.suggestedRitualInstance.destroy();
    delete section.suggestedRitualInstance;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSuggestedRitualSections);
} else {
  initSuggestedRitualSections();
}

// Theme editor support
document.addEventListener('shopify:section:load', function(event) {
  const section = event.target.querySelector('.suggested-ritual');
  if (section && !section.suggestedRitualInstance) {
    section.suggestedRitualInstance = new SuggestedRitualSection(section);
  }
});

document.addEventListener('shopify:section:unload', function(event) {
  const sectionId = event.detail.sectionId;
  cleanupSuggestedRitualSection(sectionId);
});

document.addEventListener('shopify:section:select', function(event) {
  // Optional: Add any behavior when section is selected in theme editor
});

document.addEventListener('shopify:section:deselect', function(event) {
  // Optional: Add any behavior when section is deselected in theme editor
});

// Export for potential external usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SuggestedRitualSection;
}
